import React from 'react';
import { CheckCircle2, Circle, Plus, CalendarClock, User as UserIcon, X, Search, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { listTodos, createTodo, updateTodo, deleteTodo, TodoItem } from '../lib/todos';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { listContactsCore, getContactCore } from '../lib/contactsCore';
import { getClaimedContactId } from '../lib/profiles';
import { useGroupContext } from '../lib/groups/context';
import { addHistory } from '../lib/history';
import DatePicker from './DatePicker';
import { MergedEntriesComposer } from './MergedEntriesComposer';
import { uploadCompressedImage } from '../lib/imageUpload';
import { loadGoogleMapsPlaces, extractAddressParts, shortAddressLabel } from '../lib/googlePlaces';

export default function TodoPanel({ user }: { user: User }) {
    const { activeGroupId } = useGroupContext();
    const [items, setItems] = React.useState<TodoItem[]>([]);
    const [mode, setMode] = React.useState<'mine' | 'all'>('mine');
    const [loading, setLoading] = React.useState(true);
    // Inline creation panel (identical to Events add, with required Who selection)
    const [showAdd, setShowAdd] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const photoFileRef = React.useRef<HTMLInputElement | null>(null);
    // Unified new entry object mirroring EventsSection
    const [newEvt, setNewEvt] = React.useState<any>({ name: '', participants: [], notes: '', customFields: {}, entries: [] });
    // Load linked contact for default assignee requirement (placed before loaders to avoid TDZ)
    const [linkedContact, setLinkedContact] = React.useState<{ id: string; name: string; avatar?: string | null } | null>(null);
    React.useEffect(() => {
        (async () => {
            try {
                const cid = await getClaimedContactId((user as any)?.id);
                if (cid) {
                    const c = await getContactCore(supabase as any, cid);
                    if (c) setLinkedContact({ id: (c as any).id, name: `${(c as any).first_name || ''} ${(c as any).last_name || ''}`.trim() || 'Me', avatar: (c as any).profile_image_url || (c as any).photo || null });
                    return;
                }
                // fallback to local storage (Settings also persists here)
                const ls = localStorage.getItem(`gv:claimed_contact_id:${(user as any)?.id}`);
                if (ls) {
                    const c = await getContactCore(supabase as any, ls);
                    if (c) setLinkedContact({ id: (c as any).id, name: `${(c as any).first_name || ''} ${(c as any).last_name || ''}`.trim() || 'Me', avatar: (c as any).profile_image_url || (c as any).photo || null });
                }
            } catch { /* ignore */ }
        })();
    }, [user?.id]);
    // Who search (choose contact/person)
    const [whoQuery, setWhoQuery] = React.useState('');
    const [whoFocused, setWhoFocused] = React.useState(false);
    const [whoOptions, setWhoOptions] = React.useState<Array<{ id: string; name: string; city?: string | null; state?: string | null; country?: string | null; avatar?: string | null }>>([]);
    const [whoSearching, setWhoSearching] = React.useState(false);
    const [formError, setFormError] = React.useState<string | null>(null);
    // Where autocomplete
    const [placeQuery, setPlaceQuery] = React.useState('');
    const [placeSug, setPlaceSug] = React.useState<Array<{ description: string; place_id: string }>>([]);
    const [placesReady, setPlacesReady] = React.useState(false);
    const [placeSession, setPlaceSession] = React.useState<any>(null);
    const [placeFetching, setPlaceFetching] = React.useState(false);
    const [placeError, setPlaceError] = React.useState<string | null>(null);
    // Track whether a place has been committed; when true, don't show suggestions until user edits again
    const [placeCommitted, setPlaceCommitted] = React.useState(false);
    // Details custom fields (inline editor)
    const [fieldPlaceholders, setFieldPlaceholders] = React.useState<Record<string, string>>({});
    const [showAddField, setShowAddField] = React.useState(false);
    const [newFieldTitle, setNewFieldTitle] = React.useState('');
    const [newFieldPlaceholder, setNewFieldPlaceholder] = React.useState('');
    const [editingField, setEditingField] = React.useState<string | null>(null);
    const [editTitle, setEditTitle] = React.useState('');
    const [editPlaceholder, setEditPlaceholder] = React.useState('');

    // Inline completion details
    const [completingId, setCompletingId] = React.useState<string | null>(null);
    // Completion simplified: choose a date only; past/today => Mark Complete; future => Add Follow up
    const [completeDate, setCompleteDate] = React.useState<string>(''); // ISO yyyy-mm-dd or full ISO

    const load = React.useCallback(async () => {
        if (!user) return;
        setLoading(true);
        try {
            const rows = await listTodos(user.id, { includeCompleted: false, groupId: activeGroupId || null, assignedContactId: mode === 'mine' ? (linkedContact?.id || null) : null });
            setItems(rows);
        } finally {
            setLoading(false);
        }
    }, [user, activeGroupId, mode, linkedContact?.id]);

    React.useEffect(() => { load(); }, [load]);

    // Load Google Places for Where autocomplete
    React.useEffect(() => {
        let mounted = true;
        loadGoogleMapsPlaces().then(() => mounted && setPlacesReady(true)).catch(() => mounted && setPlacesReady(false));
        return () => { mounted = false; };
    }, []);

    // Debounced contact search for Who (Assignee selector for task owner)
    React.useEffect(() => {
        let active = true;
        const q = whoQuery.trim();
        if (!q) { setWhoOptions([]); return; }
        setWhoSearching(true);
        const t = setTimeout(async () => {
            try {
                const rows = await listContactsCore(supabase as any, { query: q, limit: 6 });
                if (!active) return;
                const mapped = (rows || []).map((c: any) => ({
                    id: c.id,
                    name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unknown',
                    city: c.city || null,
                    state: c.state || null,
                    country: c.country || null,
                    avatar: c.profile_image_url || c.photo || null,
                }));
                setWhoOptions(mapped);
            } catch {
                if (active) setWhoOptions([]);
            } finally {
                active && setWhoSearching(false);
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [whoQuery]);

    // Debounced Places Autocomplete predictions
    React.useEffect(() => {
        let active = true;
        const q = placeQuery.trim();
        // If user hasn't typed or place is already committed, don't suggest
        if (!q || q.length < 2 || !placesReady || placeCommitted) { setPlaceSug([]); return; }
        const t = setTimeout(() => {
            try {
                const service = new (window as any).google.maps.places.AutocompleteService();
                const req: any = { input: q, types: ['establishment', 'geocode'] };
                if (placeSession) req.sessionToken = placeSession;
                service.getPlacePredictions(req, (preds: any[]) => {
                    if (!active) return;
                    const out = (preds || []).slice(0, 6).map(p => ({ description: p.description as string, place_id: p.place_id as string }));
                    setPlaceSug(out);
                });
            } catch {
                active && setPlaceSug([]);
            }
        }, 250);
        return () => { active = false; clearTimeout(t); };
    }, [placeQuery, placesReady, placeSession, placeCommitted]);


    const handleCreate = async () => {
        setFormError(null);
        const name = (newEvt.name || '').trim();
        // Require an assignee: default to linked contact if not explicitly chosen
        const chosenAssignee = (newEvt.participants || []).find((p: any) => p.contactId);
        const assigneeContactId: string | null = chosenAssignee?.contactId || linkedContact?.id || null;
        if (!assigneeContactId) { setFormError('Set “My Contact” in Settings first to assign this follow up to yourself.'); return; }
        if (!name) { setFormError('Please enter a title'); return; }
        setSaving(true);
        try {
            // Determine due date from top-level when or first entry
            let dueISO: string | null = null;
            if (newEvt.when) {
                dueISO = new Date(newEvt.when).toISOString();
            } else if (Array.isArray(newEvt.entries) && newEvt.entries.length > 0) {
                dueISO = new Date(newEvt.entries[0].when).toISOString();
            }
            // Persist with group and assignment
            const saved = await createTodo({ title: name, due_date: dueISO, done: false, contact_id: null, event_id: null, assigned_contact_id: assigneeContactId, group_id: activeGroupId || null }, user.id);
            setItems(prev => [saved, ...prev]);
            // Log history with context so "what happened" is preserved
            try {
                // Only log history if we have a contact to associate it with
                const historyContactId = chosenAssignee?.contactId || assigneeContactId;
                if (historyContactId) {
                    await addHistory({
                        contact_id: historyContactId,
                        actor_id: (user as any)?.id || null,
                        action: 'todo_created',
                        details: {
                            title: name,
                            due_date: dueISO,
                            where: newEvt.where || null,
                            assigned_to: chosenAssignee?.name || linkedContact?.name || 'Unspecified',
                            context_note: newEvt.notes || (Array.isArray(newEvt.entries) && newEvt.entries[0]?.note) || null,
                            custom_fields: newEvt.customFields || {},
                        },
                    });
                } else {
                    console.log('🔍 Skipping history log - no contact_id available');
                }
            } catch (error) {
                console.warn('Failed to log todo creation to history:', error);
            }
            // reset panel
            setShowAdd(false);
            setNewEvt({ name: '', participants: [], notes: '', customFields: {}, entries: [] });
            setWhoQuery(''); setWhoOptions([]);
            setPlaceQuery(''); setPlaceSug([]); setPlaceError(null); setPlaceCommitted(false);
            setFieldPlaceholders({}); setShowAddField(false); setNewFieldTitle(''); setNewFieldPlaceholder(''); setEditingField(null); setEditTitle(''); setEditPlaceholder('');
        } finally {
            setSaving(false);
        }
    };
    const startComplete = (id: string) => {
        setCompletingId(prev => prev === id ? null : id);
        // default completion date to today
        try { setCompleteDate(new Date().toISOString().split('T')[0]); } catch { setCompleteDate(''); }
    };
    const confirmComplete = async (id: string) => {
        const item = items.find(i => i.id === id);
        if (!item) return;
        const updated = await updateTodo(id, { done: true });
        // Remove from list (we show only open tasks)
        setItems(prev => prev.filter(i => i.id !== id));
        // Log history against contact if available
        try {
            if (item.contact_id) {
                await addHistory({
                    contact_id: item.contact_id,
                    actor_id: (user as any)?.id || null,
                    action: 'todo_completed',
                    details: {
                        todo_id: id,
                        title: item.title,
                        outcome: 'done',
                        note: '',
                        at: completeDate ? (completeDate.includes('T') ? new Date(completeDate).toISOString() : new Date(completeDate + 'T12:00:00').toISOString()) : new Date().toISOString(),
                        event_id: item.event_id || null,
                    },
                });
            }
        } catch { /* non-blocking */ }
        // If recurring, create next instance
        try {
            const m = item.title.match(/\[(daily|weekly|monthly)\]$/i);
            if (m) {
                const freq = m[1].toLowerCase();
                const baseTitle = item.title.replace(/\s*\[(daily|weekly|monthly)\]$/i, '').trim();
                const prevDue = item.due_date ? new Date(item.due_date) : new Date();
                const next = new Date(prevDue);
                if (freq === 'daily') next.setDate(prevDue.getDate() + 1);
                if (freq === 'weekly') next.setDate(prevDue.getDate() + 7);
                if (freq === 'monthly') next.setMonth(prevDue.getMonth() + 1);
                const nextTitle = `${baseTitle} [${freq}]`;
                const created = await createTodo({ title: nextTitle, due_date: next.toISOString(), done: false, contact_id: item.contact_id || null, event_id: item.event_id || null, assigned_contact_id: item.assigned_contact_id || null, group_id: item.group_id || activeGroupId || null }, user.id);
                setItems(prev => [created, ...prev]);
            }
        } catch { /* ignore */ }
        // Reset completion UI
        setCompletingId(null);
        setCompleteDate('');
    };
    const remove = async (id: string) => {
        await deleteTodo(id); setItems(prev => prev.filter(i => i.id !== id));
    };

    const todayISO = new Date().toISOString().split('T')[0];

    // Helpers: grouping and formatting
    const toKey = (iso?: string | null) => (iso ? new Date(iso).toISOString().split('T')[0] : 'no-date');
    const formatHeader = (isoKey: string) => {
        if (isoKey === 'no-date') return 'No Date';
        const d = new Date(isoKey + 'T00:00:00');
        const day = d.getDate();
        const ord = (n: number) => {
            const s = ['th', 'st', 'nd', 'rd'];
            const v = n % 100; return s[(v - 20) % 10] || s[v] || s[0];
        };
        const month = d.toLocaleString(undefined, { month: 'long' });
        const weekday = d.toLocaleString(undefined, { weekday: 'long' });
        const year = d.getFullYear();
        return `${weekday}, ${month} ${day}${ord(day)}, ${year}`;
    };
    const grouped = React.useMemo(() => {
        const map = new Map<string, TodoItem[]>();
        for (const it of items) {
            const k = toKey(it.due_date || null);
            map.set(k, [...(map.get(k) || []), it]);
        }
        // Sort keys ascending (no-date last)
        const keys = Array.from(map.keys()).sort((a, b) => {
            if (a === 'no-date') return 1; if (b === 'no-date') return -1;
            return a.localeCompare(b);
        });
        return keys.map(k => ({ key: k, label: formatHeader(k), items: (map.get(k) || []).sort((x, y) => (x.due_date || '').localeCompare(y.due_date || '')) }));
    }, [items]);

    return (
        <div className="p-4">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-white text-lg font-semibold">Follow up</h2>
                <div className="flex items-center gap-2">
                    <div className="hidden sm:flex items-center gap-1 mr-2">
                        <button onClick={() => setMode('mine')} className={`px-2 py-1 rounded border ${mode === 'mine' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'} text-xs`}>Mine</button>
                        <button onClick={() => setMode('all')} className={`px-2 py-1 rounded border ${mode === 'all' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300'} text-xs`}>All</button>
                    </div>
                    <button onClick={() => setShowAdd(v => !v)} className="w-8 h-8 rounded-md bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center" title="New follow up">
                        <Plus className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Inline New Task panel - mirrors Events Add UI */}
            {showAdd && (
                <div className="mb-4 bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-3">
                    {/* Photo picker */}
                    <input ref={photoFileRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                            const url = await uploadCompressedImage(file, `todo-photos/${(user as any)?.id || 'misc'}`);
                            setNewEvt((prev: any) => ({ ...prev, photoUrl: url }));
                        } catch {
                            const url = URL.createObjectURL(file);
                            setNewEvt((prev: any) => ({ ...prev, photoUrl: url }));
                        }
                    }} />
                    <div className="w-full flex items-center justify-center">
                        <div className="relative">
                            <button type="button" onClick={() => photoFileRef.current?.click()} className="w-24 h-24 rounded-lg bg-gray-900 border border-gray-700 relative overflow-hidden">
                                {newEvt.photoUrl ? (
                                    <img src={newEvt.photoUrl} alt="follow up" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full" />
                                )}
                                {!newEvt.photoUrl && (
                                    <div className="absolute inset-0 grid place-items-center pointer-events-none">
                                        <div className="w-8 h-8 rounded-full bg-black/50 border border-gray-600 flex items-center justify-center">
                                            <Plus className="w-4 h-4 text-white" />
                                        </div>
                                    </div>
                                )}
                            </button>
                            {newEvt.photoUrl && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); setNewEvt((prev: any) => ({ ...prev, photoUrl: undefined })); }} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/70 border border-gray-600 text-gray-200 hover:bg-black" aria-label="Remove image" title="Remove image">×</button>
                            )}
                        </div>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-xs text-gray-400 mb-1">Follow up title</label>
                        <input value={newEvt.name || ''} onChange={(e) => setNewEvt((prev: any) => ({ ...prev, name: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" placeholder="e.g., Follow up call" />
                    </div>

                    {/* Assignee (defaults to linked contact if available) */}
                    <div>
                        <label className="block text-xs text-gray-400 mb-1">Assigned To</label>
                        <input
                            value={whoQuery}
                            onChange={(e) => setWhoQuery(e.target.value)}
                            onFocus={() => setWhoFocused(true)}
                            onBlur={() => setTimeout(() => setWhoFocused(false), 120)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    const name = whoQuery.trim(); if (name) {
                                        setNewEvt((prev: any) => {
                                            const participants = [...(prev.participants || []), { name }];
                                            const first = participants[0];
                                            return { ...prev, participants, whoName: first.name, whoContactId: first.contactId, whoAvatar: first.avatar };
                                        });
                                        setWhoQuery(''); setWhoFocused(false);
                                    }
                                }
                            }}
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                            placeholder={linkedContact ? 'Search' : 'Search'}
                        />
                        {(whoFocused || whoQuery) && whoOptions.length > 0 && (
                            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {whoOptions.slice(0, 6).map(s => (
                                    <button
                                        key={s.id}
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            setNewEvt((prev: any) => {
                                                const exists = (prev.participants || []).some((p: any) => p.contactId === s.id || p.name === s.name);
                                                const participants = exists ? [...(prev.participants || [])] : [...(prev.participants || []), { contactId: s.id, name: s.name, avatar: s.avatar || null }];
                                                const first = participants[0];
                                                return { ...prev, participants, whoName: first?.name, whoContactId: first?.contactId, whoAvatar: first?.avatar };
                                            });
                                            setWhoQuery(''); setWhoFocused(false);
                                        }}
                                        className="flex items-center gap-2 p-2 rounded-md border border-gray-700 hover:bg-gray-700 text-left"
                                    >
                                        <div className="w-8 h-8 rounded-md bg-gray-700 overflow-hidden flex-shrink-0">{s.avatar ? (<img src={s.avatar} alt={s.name} className="w-full h-full object-cover" />) : (<div className="w-full h-full bg-gray-700" />)}</div>
                                        <div className="min-w-0"><div className="text-sm text-gray-200 truncate">{s.name}</div><div className="text-xs text-gray-400 truncate">{[s.city, s.state, s.country].filter(Boolean).join(', ')}</div></div>
                                    </button>
                                ))}
                            </div>
                        )}
                        {(newEvt.participants && newEvt.participants.length > 0) && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {newEvt.participants!.map((p: any, idx: number) => (
                                    <span key={(p.contactId || p.name) + idx} className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-gray-900 border border-gray-700 text-gray-200 text-xs">
                                        {p.avatar ? (<img src={p.avatar} alt={p.name} className="w-4 h-4 rounded-full" />) : null}
                                        {p.name}
                                        <button type="button" className="ml-1 text-gray-400 hover:text-white" onClick={() => setNewEvt((prev: any) => {
                                            const arr = (prev.participants || []).filter((x: any, i: number) => i !== idx);
                                            const first = arr[0];
                                            return { ...prev, participants: arr, whoName: first?.name, whoContactId: first?.contactId, whoAvatar: first?.avatar };
                                        })}>×</button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Where */}
                    <div>
                        <label className="block text-xs text-gray-400 mb-1">Where</label>
                        <input
                            value={placeQuery || newEvt.where || ''}
                            onChange={(e) => { setPlaceCommitted(false); setPlaceQuery(e.target.value); setNewEvt((prev: any) => ({ ...prev, where: e.target.value, whereMeta: e.target.value ? { provider: 'manual', label: e.target.value } : undefined })); }}
                            onFocus={() => { try { const token = new (window as any).google.maps.places.AutocompleteSessionToken(); setPlaceSession(token); } catch { setPlaceSession(null); } }}
                            onBlur={() => { setTimeout(() => { setPlaceSug([]); setPlaceSession(null); }, 150); }}
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" placeholder="Venue or city" />
                        <div className="mt-1 flex items-center justify-between">
                            <div className="text-xs text-red-300 min-h-[1rem]">{placeError || ''}</div>
                            <button type="button" onClick={async () => {
                                setPlaceError(null); if (!navigator.geolocation) { setPlaceError('Location not supported'); return; }
                                setPlaceFetching(true);
                                try {
                                    const pos = await new Promise<GeolocationPosition>((resolve, reject) => { navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }); });
                                    await loadGoogleMapsPlaces(); const geocoder = new (window as any).google.maps.Geocoder();
                                    geocoder.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any[], status: any) => {
                                        if (status !== (window as any).google.maps.GeocoderStatus.OK || !results || results.length === 0) { setPlaceError('Could not resolve address'); setPlaceFetching(false); return; }
                                        const best = results[0]; const parts = extractAddressParts(best.address_components);
                                        const label = shortAddressLabel(null, parts.city || null, parts.state || null, parts.country || null) || best.formatted_address;
                                        setNewEvt((prev: any) => ({ ...prev, where: label, whereMeta: { provider: 'google', place_id: best.place_id || null, lat: pos.coords.latitude, lng: pos.coords.longitude, city: parts.city || null, state: parts.state || null, country: parts.country || null, label } }));
                                        setPlaceQuery(p => p || label); setPlaceFetching(false);
                                    });
                                } catch (e) { setPlaceError('Unable to get current location'); setPlaceFetching(false); }
                            }} disabled={placeFetching} className="text-xs text-blue-300 hover:text-blue-200 underline disabled:opacity-50">{placeFetching ? 'Locating…' : 'Use my location'}</button>
                        </div>
                        {placesReady && placeQuery.trim() && placeSug.length > 0 && !placeCommitted && (
                            <div className="mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-56 overflow-auto">
                                {placeSug.map(p => (
                                    <button
                                        key={p.place_id}
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            try {
                                                const svc = new (window as any).google.maps.places.PlacesService(document.createElement('div'));
                                                const req: any = { placeId: p.place_id, fields: ['name', 'geometry.location', 'address_components', 'formatted_address'] };
                                                if (placeSession) req.sessionToken = placeSession;
                                                svc.getDetails(req, (res: any, status: any) => {
                                                    if (status !== (window as any).google.maps.places.PlacesServiceStatus.OK || !res) return;
                                                    const loc = res.geometry?.location;
                                                    const parts = extractAddressParts(res.address_components);
                                                    const label = shortAddressLabel(res.name || null, parts.city || null, parts.state || null, parts.country || null) || res.formatted_address || p.description;
                                                    setNewEvt((prev: any) => ({
                                                        ...prev,
                                                        where: label,
                                                        whereMeta: {
                                                            provider: 'google',
                                                            place_id: p.place_id,
                                                            lat: loc ? loc.lat() : null,
                                                            lng: loc ? loc.lng() : null,
                                                            city: parts.city || null,
                                                            state: parts.state || null,
                                                            country: parts.country || null,
                                                            label,
                                                        }
                                                    }));
                                                    setPlaceQuery(label);
                                                    setPlaceCommitted(true);
                                                    setPlaceSug([]);
                                                });
                                            } catch {
                                                // silent
                                            }
                                        }}
                                        className="w-full text-left px-3 py-2 text-gray-200 hover:bg-gray-700 border-b border-gray-800 last:border-b-0"
                                    >
                                        {p.description}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Details inline fields */}
                    <div className="border border-gray-700 rounded-lg p-3">
                        <div className="mb-2 flex flex-col items-center text-center">
                            <div className="text-sm font-semibold text-white">Details</div>
                        </div>
                        {!showAddField && (
                            <button type="button" onClick={() => setShowAddField(true)} className="w-full mb-2 text-xs text-blue-300 hover:text-blue-200 underline">+ Add field</button>
                        )}
                        {showAddField && (
                            <div className="bg-gray-900 border border-gray-700 rounded-md p-3 mb-2">
                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-center">
                                    <div className="sm:col-span-2">
                                        <label className="block text-xs text-gray-400 mb-1">Title</label>
                                        <input value={newFieldTitle} onChange={(e) => setNewFieldTitle(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" placeholder="e.g., Where" />
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label className="block text-xs text-gray-400 mb-1">Placeholder</label>
                                        <input value={newFieldPlaceholder} onChange={(e) => setNewFieldPlaceholder(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" placeholder="e.g., Venue or city" />
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <div className="text-xs text-gray-400 mb-1">{newFieldTitle.trim() || 'Where'}</div>
                                    <div className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-500 text-sm">{newFieldPlaceholder.trim() || 'Venue or city'}</div>
                                </div>
                                <div className="mt-2 flex justify-end gap-2">
                                    <button type="button" className="px-3 py-1.5 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700" onClick={() => { setShowAddField(false); setNewFieldTitle(''); setNewFieldPlaceholder(''); }}>Cancel</button>
                                    <button type="button" className="px-3 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50" disabled={!newFieldTitle.trim()} onClick={() => {
                                        const title = newFieldTitle.trim(); if (!title) return;
                                        setNewEvt((prev: any) => ({ ...prev, customFields: { ...(prev.customFields || {}), [title]: '' } }));
                                        setFieldPlaceholders(prev => ({ ...prev, [title]: newFieldPlaceholder.trim() }));
                                        setShowAddField(false); setNewFieldTitle(''); setNewFieldPlaceholder('');
                                    }}>Add</button>
                                </div>
                            </div>
                        )}
                        <div className="space-y-3">
                            {Object.entries((newEvt as any).customFields || {}).map(([label, value]) => {
                                const isEditingThis = editingField === label;
                                return (
                                    <div key={label} className="bg-gray-900 border border-gray-700 rounded-md p-3">
                                        {!isEditingThis ? (
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <div className="text-xs text-gray-400">{label}</div>
                                                        <button type="button" className="p-1 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700" title="Edit field" onClick={() => { setEditingField(label); setEditTitle(label); setEditPlaceholder(fieldPlaceholders[label] || `Enter ${String(label).toLowerCase()}`); }}>
                                                            <Pencil className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button type="button" className="p-1 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700" title="Delete field" onClick={() => { setNewEvt((prev: any) => { const next = { ...(prev.customFields || {}) }; delete (next as any)[label]; return { ...prev, customFields: next }; }); setFieldPlaceholders(prev => { const n = { ...prev }; delete (n as any)[label]; return n; }); }}>
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                    <input value={String(value || '')} onChange={(e) => setNewEvt((prev: any) => ({ ...prev, customFields: { ...(prev.customFields || {}), [label]: e.target.value } }))} className="mt-2 w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" placeholder={fieldPlaceholders[label] || `Enter ${String(label).toLowerCase()}`} />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-center">
                                                    <div className="sm:col-span-2">
                                                        <label className="block text-xs text-gray-400 mb-1">Title</label>
                                                        <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" />
                                                    </div>
                                                    <div className="sm:col-span-3">
                                                        <label className="block text-xs text-gray-400 mb-1">Placeholder</label>
                                                        <input value={editPlaceholder} onChange={(e) => setEditPlaceholder(e.target.value)} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600" />
                                                    </div>
                                                </div>
                                                <div className="flex justify-end gap-2">
                                                    <button type="button" className="px-3 py-1.5 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700" onClick={() => { setEditingField(null); setEditTitle(''); setEditPlaceholder(''); }}>Cancel</button>
                                                    <button type="button" className="px-3 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50" disabled={!editTitle.trim()} onClick={() => {
                                                        const nextTitle = editTitle.trim(); if (!nextTitle) return;
                                                        if (nextTitle !== label && (newEvt as any).customFields && Object.prototype.hasOwnProperty.call((newEvt as any).customFields, nextTitle)) { try { alert('A field with that title already exists.'); } catch { } return; }
                                                        setNewEvt((prev: any) => { const cur = { ...(prev.customFields || {}) }; const val = (cur as any)[label as any]; delete (cur as any)[label as any]; (cur as any)[nextTitle] = val; return { ...prev, customFields: cur }; });
                                                        setFieldPlaceholders(prev => { const n = { ...prev } as Record<string, string>; const ph = n[label] ?? `Enter ${nextTitle.toLowerCase()}`; delete (n as any)[label]; (n as any)[nextTitle] = editPlaceholder || ph; return n; });
                                                        setEditingField(null); setEditTitle(''); setEditPlaceholder('');
                                                    }}>Save</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* When + Notes (entries) */}
                    {/* When an assignee is selected (either explicitly via Who or via linked contact), switch to inline calendar for slot picking */}
                    <MergedEntriesComposer newEvt={newEvt} setNewEvt={setNewEvt} addButtonLabel="Add Follow up item" inlineCalendar={!!((newEvt.participants || []).find((p: any) => p.contactId) || linkedContact)} />

                    {/* Errors */}
                    {formError && (<div className="text-sm text-red-300">{formError}</div>)}

                    {/* Actions */}
                    <div className="flex justify-between items-center gap-2 pt-2">
                        {/* Assignee summary and quick link to Settings if missing */}
                        <div className="text-xs text-gray-300">
                            Assigned to: {' '}
                            {((newEvt.participants || []).find((p: any) => p.contactId)?.name) || linkedContact?.name || (
                                <>
                                    <button type="button" className="underline text-blue-300 hover:text-blue-200" onClick={() => {
                                        // Navigate to Settings
                                        try { window.dispatchEvent(new CustomEvent('gv:navigateToSettings' as any)); } catch { }
                                        // Fallback: update location
                                    }}>Set My Contact</button>
                                </>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => { setShowAdd(false); setNewEvt({ name: '', participants: [], notes: '', customFields: {}, entries: [] }); setWhoQuery(''); setPlaceQuery(''); setFieldPlaceholders({}); }} className="px-3 py-1.5 text-sm rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700">Cancel</button>
                            <button onClick={handleCreate} disabled={saving} className="px-3 py-1.5 text-sm rounded-md border border-gray-700 bg-gray-800 hover:bg-gray-700 text-white disabled:opacity-50">{saving ? 'Saving…' : 'Create Follow up'}</button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-gray-400 text-sm">Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-gray-400 text-sm">No open follow ups. Add one above or from events.</div>
            ) : (
                <div className="space-y-6">
                    {grouped.map(group => (
                        <div key={group.key}>
                            <div className="sticky top-0 z-10 -mx-4 px-4 py-2 bg-black/60 backdrop-blur supports-[backdrop-filter]:bg-black/40 border-b border-gray-800 flex items-center justify-between">
                                <div className="text-sm font-semibold text-white">{group.label}</div>
                                <span className="text-xs text-gray-300 px-2 py-0.5 rounded-full bg-gray-800 border border-gray-700">{group.items.length}</span>
                            </div>
                            <ul className="mt-2 space-y-2">
                                {group.items.map(i => {
                                    const recur = (i.title.match(/\[(daily|weekly|monthly)\]$/i) || [])[1];
                                    return (
                                        <li key={i.id} className="bg-gray-900 border border-gray-800 rounded-lg p-3 hover:bg-gray-800">
                                            <div className="flex items-center gap-3">
                                                <button onClick={(e) => { e.stopPropagation(); if (!i.done) startComplete(i.id); }} className="text-gray-300 hover:text-white">
                                                    {i.done ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <Circle className="w-5 h-5" />}
                                                </button>
                                                <div className="flex-1 min-w-0">
                                                    <div className={`text-gray-200 ${i.done ? 'line-through opacity-70' : ''}`}>{i.title}</div>
                                                    <div className="text-xs text-gray-400 flex items-center gap-2">
                                                        {recur ? <><CalendarClock className="w-3 h-3" /><span className="px-1.5 py-0.5 rounded bg-gray-800 border border-gray-700">{recur}</span></> : null}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {i.event_id && (
                                                        <button
                                                            className="p-1.5 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700"
                                                            title="Open Event"
                                                            onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('gv:openEvent', { detail: { eventId: i.event_id, contactId: i.contact_id || null } } as any)); }}
                                                        >
                                                            <CalendarClock className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                    {i.contact_id && (
                                                        <button
                                                            className="p-1.5 rounded-md border border-gray-700 text-blue-300 hover:bg-gray-700 hover:text-blue-200"
                                                            title="View Person"
                                                            onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('gv:openContact' as any, { detail: { contactId: i.contact_id } } as any)); }}
                                                        >
                                                            <UserIcon className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); remove(i.id); }}
                                                        className="p-1.5 rounded-md border border-red-700 text-red-300 hover:bg-red-900/30"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                            {completingId === i.id && !i.done && (
                                                <div className="mt-3 border-t border-gray-800 pt-3">
                                                    <div className="bg-gray-800 border border-gray-700 rounded-md p-3">
                                                        <div className="text-sm text-white font-semibold mb-2">Complete Follow up</div>
                                                        <div className="mb-2">
                                                            <label className="block text-xs text-gray-400 mb-1">Choose date</label>
                                                            <DatePicker value={completeDate} onChange={(iso) => setCompleteDate(iso)} className="w-full" />
                                                        </div>
                                                        <div className="flex justify-end gap-2 mt-2">
                                                            <button onClick={() => { setCompletingId(null); setCompleteDate(''); }} className="px-3 py-1.5 rounded border border-gray-700 text-gray-300 hover:bg-gray-700 text-sm">Cancel</button>
                                                            {(() => {
                                                                // Determine if selected date is today/past vs future
                                                                let label = 'Mark as Complete';
                                                                let isFuture = false;
                                                                try {
                                                                    if (completeDate) {
                                                                        const sel = new Date(completeDate.includes('T') ? completeDate : completeDate + 'T00:00:00');
                                                                        const today = new Date();
                                                                        const selYMD = sel.toISOString().split('T')[0];
                                                                        const todayYMD = today.toISOString().split('T')[0];
                                                                        isFuture = selYMD > todayYMD;
                                                                        label = isFuture ? 'Add Follow up' : 'Mark as Complete';
                                                                    }
                                                                } catch { /* ignore */ }
                                                                return (
                                                                    isFuture ? (
                                                                        <button
                                                                            onClick={async () => {
                                                                                // Future: add another follow up with same title/contact and new due date
                                                                                try {
                                                                                    const dueISO = completeDate.includes('T') ? new Date(completeDate).toISOString() : new Date(completeDate + 'T12:00:00').toISOString();
                                                                                    const created = await createTodo({ title: i.title, due_date: dueISO, done: false, contact_id: i.contact_id || null, event_id: i.event_id || null, assigned_contact_id: i.assigned_contact_id || null, group_id: i.group_id || activeGroupId || null }, user.id);
                                                                                    setItems(prev => [created, ...prev]);
                                                                                    setCompletingId(null);
                                                                                    setCompleteDate('');
                                                                                } catch { /* ignore */ }
                                                                            }}
                                                                            className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm"
                                                                        >{label}</button>
                                                                    ) : (
                                                                        <button onClick={() => confirmComplete(i.id)} className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm">{label}</button>
                                                                    )
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
            {/* No modal; creation handled inline above */}
        </div>
    );
}
